import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useLanguage from "../../hooks/useLanguage.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import {
  trackCTA,
  trackLead,
  trackSectionViewed,
  useSectionOnceObserver,
} from "../../lib/analytics";
import { buildSandboxUrl } from "../../lib/sandboxDemo.js";
import { WPL_SECTION } from "./sections.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * Closing statement.
 *
 * Two actions, in the order a visitor from search actually wants them: the
 * price, and — for whoever is not ready to look at a price — the sandbox, which
 * is the cheapest possible next step and the one thing on the page that answers
 * "is it any good?" without a purchase.
 *
 * `trackLead` fires on the sandbox action but NOT on "see the plans": a scroll
 * to the pricing section is a CTA click and nothing more, while `/try`
 * provisions a real disposable Heirloom event — which is the destination the
 * homepage pricing card already counts as a lead.
 */
const PlanningFinalCta = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const { shortLanguage } = useLanguage();
  const analyticsContext = useAnalyticsContext();
  const scrollToPlans = useScrollToHomeSection("pricing");

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(WPL_SECTION.FINAL_CTA, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section wps-section wps-final">
      <div className="container">
        <div className="wps-final-inner">
          <h2 className="heading-136">
            {t("weddingPlanning.finalCta.heading")}
          </h2>
          <p className="body-20 wps-final-body">
            {t("weddingPlanning.finalCta.body")}
          </p>
          <Button
            href={localize("/#pricing")}
            text={t("weddingPlanning.finalCta.ctaPrimary")}
            className="button-lg"
            onClick={(e) => {
              trackCTA("wedding_planning_final_cta", {
                ...analyticsContext,
                ctaName: "see_plans",
              });
              scrollToPlans(e);
            }}
          />
          <p className="dwi-final-secondary">
            <a
              className="wps-text-link"
              href={buildSandboxUrl(shortLanguage)}
              target="_blank"
              rel="noopener noreferrer nofollow"
              onClick={() => {
                const ctx = { ...analyticsContext, ctaName: "start_trial" };
                trackCTA("wedding_planning_final_cta", ctx);
                trackLead("wedding_planning_final_cta", ctx);
              }}
            >
              {t("weddingPlanning.finalCta.ctaSecondary")}
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PlanningFinalCta;
